import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Welcome from "./pages/Welcome";
import Dashboard from "./pages/Dashboard";
import Tasks from "./pages/Tasks";
import Clients from "./pages/Clients";
import Billing from "./pages/Billing";
import Masters from "./pages/Masters";
import Users from "./pages/Users";
import Reports from "./pages/Reports";
import Profile from "./pages/Profile";
import TimeTracking from "./pages/TimeTracking";
import Workflows from "./pages/Workflows";
import FileRegistry from "./pages/FileRegistry";
import BookCall from "./pages/BookCall";
import RecurringTasks from "./pages/RecurringTasks";
import Enquiries from "./pages/Enquiries";
import Reminders from "./pages/Reminders";
import AuditModule from "./pages/AuditModule";
import "./App.css";

// Component to handle root route - show Welcome if not logged in, Dashboard if logged in
const RootRedirect = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <Welcome />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/welcome" element={<Welcome />} />
          <Route path="/login" element={<Login />} />
          
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/tasks"
            element={
              <ProtectedRoute>
                <Layout>
                  <Tasks />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/clients"
            element={
              <ProtectedRoute roles={['partner', 'manager']}>
                <Layout>
                  <Clients />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/billing"
            element={
              <ProtectedRoute roles={['partner', 'manager']}>
                <Layout>
                  <Billing />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/masters"
            element={
              <ProtectedRoute roles={['partner', 'manager']}>
                <Layout>
                  <Masters />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/users"
            element={
              <ProtectedRoute roles={['partner']}>
                <Layout>
                  <Users />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/enquiries"
            element={
              <ProtectedRoute roles={['partner', 'manager']}>
                <Layout>
                  <Enquiries />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/reminders"
            element={
              <ProtectedRoute roles={['partner', 'manager', 'staff']}>
                <Layout>
                  <Reminders />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/reports"
            element={
              <ProtectedRoute roles={['partner', 'manager']}>
                <Layout>
                  <Reports />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/reports/client/:clientId"
            element={
              <ProtectedRoute>
                <Layout>
                  <Reports />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Layout>
                  <Profile />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/time-tracking"
            element={
              <ProtectedRoute roles={['partner', 'manager']}>
                <Layout>
                  <TimeTracking />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/workflows"
            element={
              <ProtectedRoute>
                <Layout>
                  <Workflows />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/file-registry"
            element={
              <ProtectedRoute>
                <Layout>
                  <FileRegistry />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/book-call"
            element={
              <ProtectedRoute>
                <Layout>
                  <BookCall />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/recurring-tasks"
            element={
              <ProtectedRoute roles={['partner', 'manager']}>
                <Layout>
                  <RecurringTasks />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/audit"
            element={
              <ProtectedRoute roles={['partner', 'manager', 'staff']}>
                <Layout>
                  <AuditModule />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
